import { waitForProfile } from '@/utils/member-split/wait-for-profile';
import { MY_GIVING_WAITING_FOR_LINK_PATH } from '@/constants/MyGiving/waiting-for-link';

const MY_GIVING_PATH = /^\/my-giving(\/|$)/;

/**
 * Keeps a login with no member out of MyGiving (member/app-user split, phase 4b).
 *
 * Giving is a member's: giving-api has no PIN, records or banks for a login its
 * CG leader has not linked, so the PIN prompt it would otherwise reach can only
 * fail (403). Every /my-giving route sends such a login to a page that says
 * MyGiving opens once the link is made; a member who reaches that page (linked
 * since, or an old link) goes on to MyGiving.
 *
 * The profile may still be loading on a cold start, when `hasNoMember` reads false
 * for everyone, so the guard waits for it first. A failed load falls through to the
 * member path, as before.
 *
 * Registered as a global `beforeEach`, after token priming, rather than on each
 * route, so the PIN guard and the pledge guards stay as they are.
 */
export function createMyGivingLinkGuard(store) {
  return async to => {
    if (!MY_GIVING_PATH.test(to.path)) return true;
    // Signed out: the auth guard sends this to login. Loading a profile without a
    // token would only fail, and toast "Failed to load profile." on the way.
    if (!store.state.user?.token) return true;

    await waitForProfile(store.state.profile, store.dispatch);
    const hasNoMember = store.state.profile?.hasNoMember === true;
    const isWaitingPage = to.path === MY_GIVING_WAITING_FOR_LINK_PATH;

    if (hasNoMember && !isWaitingPage) {
      return { path: MY_GIVING_WAITING_FOR_LINK_PATH, replace: true };
    }
    if (!hasNoMember && isWaitingPage && store.state.profile?.id) {
      return { path: '/my-giving', replace: true };
    }
    return true;
  };
}
